import { useEffect, useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import {
  Dropdown,
  DropdownChip,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
} from '@mesh.ai/rabbit';
import { L3_PATHS } from 'Deer/constants/routes';
import { useQuery } from 'components/hooks/useQuery';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setSelectedReviewCycle, useReviewManageSidepanelData } from 'reducers/reviewManageSidepanelSlice';
import useReviewSectionsData from 'screens/performanceReview/LandingPage/hooks/dataHooks';

import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { REVIEW_TABLE_TYPES } from './IntentDropdown';

const getSelectedReviewSectionData = ({ reviewsSectionsData, selectedTableId }) =>
  reviewsSectionsData?.find(({ sectionId }) => sectionId === selectedTableId)?.sectionData ?? null;

const getRouteHelper = ({ activeTableId, selectedReviewCycleId }) =>
  `${L3_PATHS['my-data'].review.myReviews}?activeTableId=${activeTableId}&reviewCycleId=${selectedReviewCycleId}`;

export const LandingPageReviewCycleDropdown = () => {
  const { activeTableId = '', reviewCycleId = '' } = useQuery();
  const { selectedReviewCycle: reduxSelectedReviewCycle } = useReviewManageSidepanelData();

  const history = useHistory();
  const isReviewLandingPages = history.location.pathname.includes(L3_PATHS['my-data'].review.myReviews);

  const dispatch = useDispatch();
  // 'null' string(string because it's read from query param) is used to represent the first option in the dropdown i.e. 'all review cycles'
  const numberReviewCycleId = ['', 'null'].includes(reviewCycleId)
    ? reduxSelectedReviewCycle?.id || null
    : Number(reviewCycleId);

  const selectedTableId = activeTableId !== '' ? activeTableId : null;

  const {
    data: reviewsSectionsData = [],
    isLoading: isLoadingReviewSectionsData,
    isSuccess: isSuccessReviewSectionsData,
  } = useReviewSectionsData();

  const selectedReviewSectionData = getSelectedReviewSectionData({
    reviewsSectionsData,
    selectedTableId,
  });

  const onReviewCycleChange = (reviewCycleId_, label_) => {
    history.push(
      getRouteHelper({
        activeTableId,
        selectedReviewCycleId: reviewCycleId_,
      })
    );
    // case for first option: all review cycles
    if (reviewCycleId_ === null) {
      dispatch(setSelectedReviewCycle(null));
      return;
    }
    dispatch(
      setSelectedReviewCycle({
        id: reviewCycleId_,
        name: label_,
        // converting to string just ot keep type consistent with the options set in review manage page
        uuid: `${reviewCycleId_}`,
        displayName: label_,
      })
    );
  };

  const reviewCycleOptions = useMemo(() => {
    const options = (selectedReviewSectionData?.reviewCycles ?? []).map(({ id, name, formsCount }) => ({
      value: id,
      label: `${name} (${formsCount})`,
    }));
    return options;
  }, [selectedReviewSectionData?.reviewCycles]);

  const selectedReviewCycle = reviewCycleOptions.find(({ value }) => value === numberReviewCycleId) ?? null;

  useEffect(() => {
    if (!isReviewLandingPages || !selectedTableId || selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS) return;

    // if queryparam review cycle id or redux id is not valid, then reset it to first in available review cycles
    if (numberReviewCycleId && isSuccessReviewSectionsData && !selectedReviewCycle) {
      history.push(
        getRouteHelper({
          activeTableId,
          selectedReviewCycleId: reviewCycleOptions?.[0]?.value,
        })
      );
    }

    // make query param in sync with redux selected review cycle if it's a valid review cycle
    if (
      isSuccessReviewSectionsData &&
      reduxSelectedReviewCycle &&
      Number(reviewCycleId) !== reduxSelectedReviewCycle.id
    ) {
      history.push(
        getRouteHelper({
          activeTableId,
          selectedReviewCycleId: reduxSelectedReviewCycle.id,
        })
      );
    }

    // if the redux selected review cycle is not available in the current review cycle options, then reset persisted value to null
    if (
      isSuccessReviewSectionsData &&
      reduxSelectedReviewCycle &&
      selectedReviewCycle?.value !== reduxSelectedReviewCycle.id
    ) {
      dispatch(setSelectedReviewCycle(null));
    }

    // if no persisted review cycle in redux, but the url has review cycle id, then set the selected review cycle in redux
    if (isSuccessReviewSectionsData && !reduxSelectedReviewCycle && numberReviewCycleId && selectedReviewCycle) {
      dispatch(
        setSelectedReviewCycle({
          id: numberReviewCycleId,
          name: selectedReviewCycle?.label,
          uuid: `${numberReviewCycleId}`,
          displayName: selectedReviewCycle?.label,
        })
      );
    }
  }, [
    activeTableId,
    dispatch,
    history,
    isReviewLandingPages,
    isSuccessReviewSectionsData,
    numberReviewCycleId,
    reduxSelectedReviewCycle,
    reviewCycleId,
    selectedReviewCycle,
    selectedTableId,
  ]);

  if (isLoadingReviewSectionsData) {
    return (
      <Flex alignItems="center" flexGrow={1}>
        <SkeletonLoader skeletons={['10px']} />
      </Flex>
    );
  }

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <DropdownChip className="chip-dropdown">
          <TextBodyMedium bold color="text.g2" className="group-data[state=open]:text-[#004F67]">
            Cycle :
          </TextBodyMedium>

          <TextBodyMedium bold color="text.g1">
            {selectedReviewCycle?.label ?? ''}
          </TextBodyMedium>
        </DropdownChip>
      </DropdownTrigger>
      <DropdownContent
        align="start"
        style={{
          maxHeight: '280px',
          overflowY: 'auto',
        }}
      >
        <DropdownRadioGroup value={selectedReviewCycle?.value}>
          {reviewCycleOptions?.map(({ label, value }) => (
            <DropdownRadioItem key={value} value={value} onClick={() => onReviewCycleChange(value, label)}>
              {label}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      </DropdownContent>
    </Dropdown>
  );
};
